/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 * @generated-id: 4ff8dcd540f6
 */

import { CloudinaryAssetMgmtCore } from "../core.js";
import { encodeJSON, encodeSimple } from "../lib/encodings.js";
import { compactMap } from "../lib/primitives.js";
import { safeParse } from "../lib/schemas.js";
import { RequestOptions } from "../lib/sdks.js";
import { extractSecurity, resolveGlobalSecurity } from "../lib/security.js";
import { pathToFunc } from "../lib/url.js";
import { APIError } from "../models/errors/apierror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import {
  UploadRequestRequest,
  UploadRequestRequest$zodSchema,
} from "../models/uploadop.js";
import { UploadRequest } from "../models/uploadrequest.js";
import { UploadResourceType } from "../models/uploadresourcetype.js";
import { APICall, APIPromise } from "../types/async.js";
import { Result } from "../types/fp.js";

export enum UploadAcceptEnum {
  applicationJsonAccept = "application/json",
  textHtmlAccept = "text/html",
}

/**
 * Uploads media assets (images, videos, raw files) to your Cloudinary product environment
 *
 * @remarks
 * Uploads media assets (images, videos, raw files) to your Cloudinary product environment. The file is securely stored
 * in the cloud with backup and revision history. Cloudinary automatically analyzes and saves important data about each
 * asset, such as format, size, resolution, and prominent colors, which is indexed to enable searching on those attributes.
 *
 * Supports uploading from:
 * - Local file paths (SDKs/MCP server only). For MCP server path MUST start with file://
 * - Remote HTTP/HTTPS URLs
 * - Base64 Data URIs (max ~60 MB)
 * - Private storage buckets (S3 or Google Storage)
 * - FTP addresses
 *
 * The uploaded asset is immediately available for transformation and delivery upon successful upload.
 *
 * Transform media files using transformation syntax in delivery URLs, which creates derived files accessible immediately without re-uploading the original.
 */
export function uploadUpload(
  client$: CloudinaryAssetMgmtCore,
  resource_type: UploadResourceType | undefined,
  upload_request: UploadRequest,
  options?: RequestOptions,
): APIPromise<
  Result<
    Response,
    | APIError
    | SDKValidationError
    | UnexpectedClientError
    | InvalidRequestError
    | RequestAbortedError
    | RequestTimeoutError
    | ConnectionError
  >
> {
  return new APIPromise($do(
    client$,
    resource_type,
    upload_request,
    options,
  ));
}

async function $do(
  client$: CloudinaryAssetMgmtCore,
  resource_type: UploadResourceType | undefined,
  upload_request: UploadRequest,
  options?: RequestOptions & { acceptHeaderOverride?: UploadAcceptEnum },
): Promise<
  [
    Result<
      Response,
      | APIError
      | SDKValidationError
      | UnexpectedClientError
      | InvalidRequestError
      | RequestAbortedError
      | RequestTimeoutError
      | ConnectionError
    >,
    APICall,
  ]
> {
  const input$: UploadRequestRequest = {
    resource_type: resource_type,
    upload_request: upload_request,
  };

  const parsed$ = safeParse(
    input$,
    (value$) => UploadRequestRequest$zodSchema.parse(value$),
    "Input validation failed",
  );
  if (!parsed$.ok) {
    return [parsed$, { status: "invalid" }];
  }
  const payload$ = parsed$.value;
  const body$ = encodeJSON("body", payload$.upload_request, { explode: true });

  const pathParams$ = {
    cloud_name: encodeSimple("cloud_name", client$._options.cloud_name, {
      explode: false,
      charEncoding: "percent",
    }),
    resource_type: encodeSimple("resource_type", payload$.resource_type, {
      explode: false,
      charEncoding: "percent",
    }),
  };
  const path$ = pathToFunc("/v1_1/{cloud_name}/{resource_type}/upload")(
    pathParams$,
  );

  const headers$ = new Headers(compactMap({
    "Content-Type": "application/json",
    Accept: options?.acceptHeaderOverride
      || "application/json;q=1, text/html;q=0",
  }));
  const securityInput = await extractSecurity(client$._options.security);
  const requestSecurity = resolveGlobalSecurity(securityInput);

  const context = {
    options: client$._options,
    baseURL: options?.serverURL ?? client$._baseURL ?? "",
    operationID: "upload",
    oAuth2Scopes: null,
    resolvedSecurity: requestSecurity,
    securitySource: client$._options.security,
    retryConfig: options?.retries
      || client$._options.retryConfig
      || { strategy: "none" },
    retryCodes: options?.retryCodes || [
      "429",
      "500",
      "502",
      "503",
      "504",
    ],
  };

  const requestRes = client$._createRequest(context, {
    security: requestSecurity,
    method: "POST",
    baseURL: options?.serverURL,
    path: path$,
    headers: headers$,
    body: body$,
    userAgent: client$._options.userAgent,
    timeoutMs: options?.timeoutMs || client$._options.timeoutMs
      || -1,
  }, options);
  if (!requestRes.ok) {
    return [requestRes, { status: "invalid" }];
  }
  const req$ = requestRes.value;

  const doResult = await client$._do(req$, {
    context,
    errorCodes: [],
    retryConfig: context.retryConfig,
    retryCodes: context.retryCodes,
  });
  if (!doResult.ok) {
    return [doResult, { status: "request-error", request: req$ }];
  }
  return [doResult, {
    status: "complete",
    "request": req$,
    response: doResult.value,
  }];
}
